import { Form, InputNumber } from 'antd';
import Box from 'components/Box';
import Text from 'components/Text';
import styled from 'styled-components';
import SwitchV2 from 'components/SwitchV2';
import { useEffect, useRef, useState } from 'react';
import Button from 'components/Button';
import { getMessageError, IResponseDataBody } from 'services/response';
import settingApis from 'features/settingService/services/apis';
import toast from 'utils/toast';
import { LOYALTY_STAMPS_REWARD_TYPE } from 'features/settingService/services/constants';
import useGetLoyaltyStampsConfig from 'features/settingService/services/hooks/useGetLoyaltyStampsConfig';
import { RewardTier } from 'features/settingService/services/types/api';
import CurrencyInputPrecision from 'components/CurrencyInputPrecision';
import colorTheme from 'constants/color';
import Add from 'assets/Icons/add';
import ChangeServiceModal, {
  ChangeServiceModalRef,
} from 'features/ticketList/pages/TicketDetailsPage/components/ChangeServiceModal';
import RewardRow from './RewardRow';

interface LoyaltyStampFormValues {
  enabled: boolean;
  minimumTicketAmount: number;
  stampsEarned: number;
  rewards: RewardTier[];
}

const LoyaltyStamps = () => {
  const { data, mutate } = useGetLoyaltyStampsConfig();
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm<LoyaltyStampFormValues>();
  const useChangeServiceModalRef = useRef<ChangeServiceModalRef>(null);

  useEffect(() => {
    if (!data) return;

    form.setFieldsValue({
      enabled: data.enabled,
      minimumTicketAmount: data.minimumTicketAmount,
      stampsEarned: data.stampsEarned,
      rewards: data.rewards.map((reward) => ({
        id: reward.id,
        stampsRequired: reward.stampsRequired,
        rewardType: reward.rewardType,
        discountValue: reward.discountValue,
        serviceId: reward.serviceId,
        serviceName: reward.serviceName,
      })),
    });
  }, [data]);

  const handleSubmit = async (values: LoyaltyStampFormValues) => {
    setLoading(true);
    try {
      const res: IResponseDataBody =
        await settingApis.updateLoyaltyStampsConfig({
          enabled: values.enabled,
          minimumTicketAmount: values.minimumTicketAmount,
          stampsEarned: values.stampsEarned,
          rewards: values.rewards.map((reward) => ({
            id: reward.id,
            stampsRequired: reward.stampsRequired,
            rewardType: reward.rewardType,
            discountValue: reward.discountValue,
            serviceId: reward.serviceId,
          })),
        });

      if (res?.data?.data) {
        toast.success('Update Loyalty Stamps Config successfully');
        mutate();
      }
    } catch (error) {
      toast.error(
        getMessageError(error) || 'Update Loyalty Stamps config failed',
      );
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    if (data) {
      form.setFieldsValue({
        enabled: data.enabled,
        minimumTicketAmount: data.minimumTicketAmount,
        stampsEarned: data.stampsEarned,
        rewards: data.rewards,
      });
    }
  };

  return (
    <Container>
      <Form
        form={form}
        layout='vertical'
        className='receipt-form'
        onFinish={handleSubmit}
      >
        <Wrapper>
          <LoyaltyHeader>
            <Form.Item name='enabled' valuePropName='checked' noStyle>
              <SwitchV2 />
            </Form.Item>
            <Text variant='H4' style={{ fontWeight: 500 }}>
              Loyalty Stamp&apos;s Program
            </Text>
          </LoyaltyHeader>
          <Form.Item noStyle shouldUpdate>
            {(form) => {
              const enabled = form.getFieldValue('enabled');
              return (
                enabled && (
                  <>
                    <EarnStamps>
                      <Text variant='H4' style={{ fontWeight: 500 }}>
                        Earn Stamps
                      </Text>
                      <Box width='100%' display='flex' gap='4'>
                        <Form.Item
                          label='Minimum ticket amount'
                          name='minimumTicketAmount'
                          style={{ flex: 1 }}
                        >
                          <CurrencyInputPrecision size='medium' />
                        </Form.Item>
                        <Form.Item
                          label='Stamps earned'
                          name='stampsEarned'
                          className='equal-point'
                          style={{ flex: 1 }}
                        >
                          <InputNumber
                            min={1}
                            controls={false}
                            style={{ flex: 1 }}
                          />
                        </Form.Item>
                      </Box>
                    </EarnStamps>
                    <RedeemRewards>
                      <Text variant='H4' style={{ fontWeight: 500 }}>
                        Redeem Rewards
                      </Text>
                      <div className='reward-header'>
                        <div>STAMPS REQUIRED</div>
                        <div>REWARD TYPE</div>
                        <div>DISCOUNT VALUE</div>
                        <div>ACTIONS</div>
                      </div>
                      {/* Form.List */}
                      <Form.List name='rewards'>
                        {(fields, { add, remove }) => (
                          <>
                            {fields.map((field) => (
                              <RewardRow
                                key={field.key}
                                field={field}
                                remove={remove}
                                onClickChooseService={() =>
                                  useChangeServiceModalRef.current?.open(
                                    (_, serviceObject) => {
                                      form.setFieldValue(
                                        ['rewards', field.name, 'serviceId'],
                                        serviceObject?.id || '',
                                      );
                                      form.setFieldValue(
                                        ['rewards', field.name, 'serviceName'],
                                        serviceObject?.serviceName || null,
                                      );
                                    },
                                    form.getFieldValue([
                                      'rewards',
                                      field.name,
                                      'serviceId',
                                    ])
                                  )
                                }
                              />
                            ))}

                            <Button
                              ntype='DARK_BLUE'
                              cardCashier
                              iconElement={
                                <Add fill='#fff' width='40' height='40' />
                              }
                              onClick={() =>
                                add({
                                  stampsRequired: undefined,
                                  rewardType:
                                    LOYALTY_STAMPS_REWARD_TYPE.AMOUNT_DISCOUNT,
                                  discountValue: undefined,
                                })
                              }
                              className='add-reward-button'
                            >
                              Add Reward Tier
                            </Button>
                          </>
                        )}
                      </Form.List>
                    </RedeemRewards>
                  </>
                )
              );
            }}
          </Form.Item>
        </Wrapper>
      </Form>
      <BottomAction>
        <Button
          ntype='SECONDARY'
          cardCashier
          htmlType='button'
          width='10rem'
          onClick={handleReset}
          disabled={loading}
        >
          Reset
        </Button>
        <Button
          ntype='PRIMARY'
          cardCashier
          htmlType='button'
          width='10rem'
          onClick={() => form.submit()}
          disabled={loading}
        >
          Save
        </Button>
      </BottomAction>
      <ChangeServiceModal ref={useChangeServiceModalRef} />
    </Container>
  );
};

export default LoyaltyStamps;

const Container = styled.div`
  width: 100%;
  height: 100%;

  .receipt-form {
    width: 100%;
    height: calc(100% - 90px);
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding: 24px;
    background: #fff;
    font-family: Poppins;
  }
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px;
  background: #f6f7fc;
  border-radius: 4px;
  border: 1px solid #ccd4dc;

  .ant-form-item {
    margin-bottom: 0;
  }

  .ant-form-item-label > label {
    font-weight: 400;
    font-size: 24px;
    font-family: Poppins;
  }
  .ant-input-number {
    width: 100%;
  }
  .ant-input-number-input {
    font-family: Poppins;
    font-size: 24px;
    font-weight: 500;
    height: 3.5rem;
  }

  .ant-select-selection-item {
    font-family: Poppins;
    font-size: 24px;
    font-weight: 500;
  }

  .ant-input {
    font-family: Poppins;
    font-size: 24px;
    font-weight: 500;
  }
`;

const LoyaltyHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;

  .ant-switch {
    zoom: 1.6;
  }
`;

const EarnStamps = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  border-radius: 8px;
  background: #ffffff;
  padding: 16px;

  .equal-point {
    .ant-input {
      flex: 1;
      height: 3.5rem;
    }
  }
`;

const RedeemRewards = styled.div`
  display: flex;
  padding: 16px;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  border-radius: 8px;
  background: #ffffff;

  .reward-header {
    width: 100%;
    display: grid;
    grid-template-columns: 2fr 2fr 2fr 0.8fr;
    align-items: center;
    gap: 16px;
    padding: 8px 16px;

    background: ${colorTheme.v2_secondary_1};
    border-radius: 6px 6px 0 0;

    font-size: 24px;
    font-weight: 600;
    color: ${colorTheme.v2_text_4};

    box-sizing: border-box;
  }

  .reward-row {
    width: 100%;
    display: grid;
    grid-template-columns: 2fr 2fr 2fr 0.8fr;
    align-items: start;
    padding: 0px 16px;
    border-radius: 0;
    gap: 16px;
    box-sizing: border-box;

    .discount-input {
      width: 100%;
    }

    .ant-input-number-group-addon {
      font-family: Poppins;
      font-size: 24px;
      font-weight: 500;
      background: ${colorTheme.v2_neutral_3};
      padding: 0 20px;
    }
  }

  .add-reward-button {
    height: 4rem!important;
  }

  .CONTENT_2 {
    font-size: 20px;
    font-family: Poppins;
  }
`;

const BottomAction = styled.div`
  position: sticky;
  bottom: 0;
  z-index: 10;
  padding: 16px 24px;
  display: flex;
  justify-content: flex-end;
  gap: 24px;
  background: #f1f5f9;
  box-shadow: 0 -4px 8px 0 rgba(0, 0, 0, 0.15);

  .CONTENT_2 {
    font-size: 20px;
    font-weight: 600;
    font-family: Poppins;
  }
`;
